/**
 * MOB-02 pull-to-refresh orchestration:
 * SW update check → conditional skipWaiting+reload → else active-query refresh.
 * Checkout / payment routes: invalidate only — never reload.
 */

import type { QueryClient } from '@tanstack/react-query';
import { tenantRelativeMorePath } from '../../features/more/moreRouteUtils.js';
import {
  invalidateQueriesSkippingAccountMeWhenGuarded,
  refetchQueriesSkippingAccountMeWhenGuarded,
} from '../../shared/api/accountMeInvalidationGuard.js';
import { isCustomerFormDirty } from './customerFormDirtyGate.js';
import { isPaymentPending, queuePwaRefreshWhenIdle } from './paymentPendingGate.js';
import {
  applyWaitingCustomerServiceWorkerUpdate,
  updateCustomerServiceWorkerRegistration,
} from './registerPwaServiceWorker.js';

/** sessionStorage key — blocks immediate PTR reload loops after SW apply. */
export const PTR_RELOAD_GUARD_KEY = 'customer:pwa:ptr-reload-guard';

/** Guard TTL after a PTR-triggered reload (ms). */
export const PTR_RELOAD_GUARD_TTL_MS = 15_000;

export type CustomerPullRefreshOutcome =
  | 'reloaded'
  | 'refetched'
  | 'invalidated'
  | 'queued-sw-reload';

export interface RunCustomerPullRefreshOptions {
  readonly queryClient: QueryClient;
  readonly pathname: string;
}

function readSessionStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage;
  } catch (err) {
    // Private mode / SecurityError — skip anti-loop persistence; still refresh data.
    if (err instanceof DOMException) {
      return null;
    }
    throw new Error(
      `sessionStorage unavailable for pull-to-refresh guard: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/** True when a PTR reload recently completed — skip another hard reload. */
export function isPtrReloadGuardActive(nowMs: number = Date.now()): boolean {
  const storage = readSessionStorage();
  if (storage === null) {
    return false;
  }
  const raw = storage.getItem(PTR_RELOAD_GUARD_KEY);
  if (raw === null) {
    return false;
  }
  const stamped = Number(raw);
  if (!Number.isFinite(stamped)) {
    storage.removeItem(PTR_RELOAD_GUARD_KEY);
    return false;
  }
  if (nowMs - stamped > PTR_RELOAD_GUARD_TTL_MS) {
    storage.removeItem(PTR_RELOAD_GUARD_KEY);
    return false;
  }
  return true;
}

export function markPtrReloadGuard(nowMs: number = Date.now()): void {
  const storage = readSessionStorage();
  if (storage === null) {
    return;
  }
  storage.setItem(PTR_RELOAD_GUARD_KEY, String(nowMs));
}

/**
 * Checkout, handoff, post-kiosk, and live QR/claim payment surfaces —
 * data refresh only; never `location.reload()`.
 */
export function isPullRefreshReloadForbiddenPath(pathname: string): boolean {
  const rel = tenantRelativeMorePath(pathname);
  if (rel === '/checkout' || rel.startsWith('/checkout/')) {
    return true;
  }
  if (rel === '/handoff' || rel.startsWith('/handoff/')) {
    return true;
  }
  if (rel.startsWith('/post-kiosk')) {
    return true;
  }
  if (rel.startsWith('/account/payments/claim')) {
    return true;
  }
  if (/^\/account\/payments\/[^/]+\/qr(?:\/|$)/.test(rel)) {
    return true;
  }
  return false;
}

/**
 * Lock-free orchestration (caller owns in-flight via usePullToRefresh).
 * Prefer SW waiting → skipWaiting+reload when safe; otherwise refetch/invalidate.
 */
export async function runCustomerPullRefresh(
  options: RunCustomerPullRefreshOptions,
): Promise<CustomerPullRefreshOutcome> {
  const { queryClient, pathname } = options;
  const reloadForbidden = isPullRefreshReloadForbiddenPath(pathname);
  const guardActive = isPtrReloadGuardActive();

  const { registration, hasWaiting } = await updateCustomerServiceWorkerRegistration();

  if (
    hasWaiting &&
    registration !== null &&
    !reloadForbidden &&
    !guardActive
  ) {
    const refresh = (): void => {
      markPtrReloadGuard();
      applyWaitingCustomerServiceWorkerUpdate(registration);
    };

    // Mid-payment: never hard-reload; queue SW apply and refresh data only.
    if (isPaymentPending()) {
      queuePwaRefreshWhenIdle(refresh);
      // G17 — never refetch accountMe while guest / missing_cred latch.
      await refetchQueriesSkippingAccountMeWhenGuarded(queryClient, {
        type: 'active',
      });
      return 'queued-sw-reload';
    }

    // Dirty drafts: no silent form loss — invalidate only (gesture also gates via isDisabled).
    if (isCustomerFormDirty()) {
      await invalidateQueriesSkippingAccountMeWhenGuarded(queryClient, {
        type: 'active',
      });
      return 'invalidated';
    }

    refresh();
    return 'reloaded';
  }

  if (reloadForbidden) {
    await invalidateQueriesSkippingAccountMeWhenGuarded(queryClient, {
      type: 'active',
    });
    return 'invalidated';
  }

  await refetchQueriesSkippingAccountMeWhenGuarded(queryClient, {
    type: 'active',
  });
  return 'refetched';
}
